import React, { useState, useEffect, useCallback } from 'react';
import { getUsers, createUser, updateUser, deleteUser, resetUserPassword } from '../../services/api';
import { Button } from '../ui/button';
import { ConfirmModal } from '../ui/ConfirmModal';
import { UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { UserTable } from './users/UserTable';
import { UserFormModal, PasswordResetModal } from './users/UserModals';

export const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, user: null });
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formData, setFormData] = useState({ username: '', email: '', password: '', role: 'admin' });

  const fetchUsers = useCallback(async () => {
    try { setUsers((await getUsers()).data); }
    catch { toast.error('Failed to fetch users'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({ username: user.username, email: user.email, role: user.role, password: '' });
    } else {
      setEditingUser(null);
      setFormData({ username: '', email: '', password: '', role: 'admin' });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setShowPassword(false);
    setFormData({ username: '', email: '', password: '', role: 'admin' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await updateUser(editingUser.id, { username: formData.username, email: formData.email, role: formData.role });
        toast.success('User updated successfully');
      } else {
        if (!formData.password || formData.password.length < 6) {
          toast.error('Password must be at least 6 characters');
          return;
        }
        await createUser(formData);
        toast.success('User created successfully');
      }
      fetchUsers();
      handleCloseModal();
    } catch (error) { toast.error(error.response?.data?.detail || 'Operation failed'); }
  };

  const handleDelete = async () => {
    try { await deleteUser(deleteModal.user.id); toast.success('User deleted'); fetchUsers(); }
    catch { toast.error('Failed to delete user'); }
  };

  const handleToggleStatus = async (user) => {
    try { await updateUser(user.id, { is_active: !user.is_active }); toast.success(`User ${user.is_active ? 'deactivated' : 'activated'}`); fetchUsers(); }
    catch { toast.error('Failed to update user status'); }
  };

  const handleOpenPasswordModal = (user) => {
    setResetPasswordUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setShowPasswordModal(true);
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setResetPasswordUser(null);
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    try {
      await resetUserPassword(resetPasswordUser.id, newPassword);
      toast.success(`Password reset successfully for ${resetPasswordUser.username}`);
      handleClosePasswordModal();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to reset password'); }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><div className="text-2xl text-orange-400 animate-pulse">Loading users...</div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-green-400">User Management</h1>
          <p className="text-gray-400 mt-2">Manage admin users and permissions</p>
        </div>
        <Button onClick={() => handleOpenModal()} className="bg-gradient-to-r from-orange-500 to-green-500 hover:from-orange-600 hover:to-green-600">
          <UserPlus className="w-4 h-4 mr-2" /> Add User
        </Button>
      </div>

      <UserTable users={users} onEdit={handleOpenModal} onResetPassword={handleOpenPasswordModal}
        onDelete={(user) => setDeleteModal({ isOpen: true, user })} onToggleStatus={handleToggleStatus} />

      {showModal && (
        <UserFormModal formData={formData} setFormData={setFormData} editingUser={editingUser}
          showPassword={showPassword} setShowPassword={setShowPassword} onSubmit={handleSubmit} onClose={handleCloseModal} />
      )}

      {showPasswordModal && (
        <PasswordResetModal user={resetPasswordUser} newPassword={newPassword} setNewPassword={setNewPassword}
          confirmPassword={confirmPassword} setConfirmPassword={setConfirmPassword}
          onSubmit={handleResetPassword} onClose={handleClosePasswordModal} />
      )}

      <ConfirmModal isOpen={deleteModal.isOpen} onClose={() => setDeleteModal({ isOpen: false, user: null })}
        onConfirm={handleDelete} title="Confirm Delete"
        message={`Are you sure you want to delete user "${deleteModal.user?.username}"? This action cannot be undone.`}
        confirmText="Delete" cancelText="Cancel" />
    </div>
  );
};
